import _ from "lodash";
import moment from "moment";
import { ATTENDANCE_CONSTANTS } from "AttendanceDashboard/constants/stringConstants";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { getMemoizedItemsFunction } from "./GetMemoizedItemsFunction";

export const DAYS_IN_ORDER = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const getWeekDaysOptions = (fullWeekDayName = false) => {
  const localLocale = moment();
  localLocale.locale("en");
  const startOfWeekLocal = localLocale.startOf("isoWeek"),
    startOfWeek = moment().startOf("isoWeek"),
    options = [];
  let count = 1,
    currVal,
    currLabel;
  const format = fullWeekDayName ? "dddd" : "ddd";
  while (count <= 7) {
    currLabel = startOfWeek.format(format);
    currVal = startOfWeekLocal.format(format);
    options.push({
      label: currLabel,
      value: currVal,
    });
    startOfWeek.add(1, "day");
    startOfWeekLocal.add(1, "day");
    count++;
  }
  return options;
};

export const getAcademicYearLabel = ({
  startDate,
  endDate,
  startDateFormat = "YYYY",
  endDateFormat = "YY",
}) => {
  return (
    formatDate({ date: startDate, format: startDateFormat }) +
    "-" +
    formatDate({ date: endDate, format: endDateFormat })
  );
};

export const getWeekDaysArr = () => {
  const localLocale = moment();
  //localLocale.locale("en");
  const startOfWeek = localLocale.startOf("week"),
    options = [];
  let count = 1,
    currVal;
  while (count <= 7) {
    currVal = startOfWeek.format("ddd");
    options.push(currVal);
    startOfWeek.add(1, "day");
    count++;
  }
  return options;
};

export const getWeekDaysArrWithEnLocal = () => {
  const localLocale = moment().locale("en");
  const startOfWeek = localLocale.startOf("week");
  const options = [];

  for (let count = 1; count <= 7; count++) {
    options.push(startOfWeek.format("ddd"));
    startOfWeek.add(1, "day");
  }
  return options;
};

export const getWeekStartDate = ({ startDate, weekStartDay, weekLength }) => {
  const currentDate = moment(startDate);
  let weekStartDate = moment(currentDate)
    .startOf("week")
    .add(weekStartDay, "day");

  const today = moment(currentDate).day();

  if (today < weekStartDay) {
    weekStartDate = moment(weekStartDate).subtract(1, "week");
  }
  const weekEndDate = moment(weekStartDate).add(weekLength - 1, "day");

  if (currentDate.diff(weekEndDate, "day") > 0) {
    weekStartDate = moment(weekStartDate).add(1, "week");
  }

  return weekStartDate;
};

export const isDayOutSideRange = ({ day, maxDate, minDate }) => {
  if (!!maxDate && !!minDate) {
    return !day.isBetween(minDate, maxDate, "days", "[]");
  } else if (!!maxDate && !minDate) {
    //no min date
    return !day.isSameOrBefore(maxDate);
  } else if (!maxDate && !!minDate) {
    return !day.isSameOrAfter(minDate);
  } else {
    return false;
  }
};

export const isDayBlocked = ({ date, operationalDays }) => {
  return !_.includes(operationalDays, moment(date).locale("en").format("ddd"));
};

export const getDateMomentObject = (date = new Date()) => {
  return moment(date);
};

export const getDateMomentObjectCustom = (date = new Date(), format = "") => {
  return moment(date, format);
};

export const formatDate = ({ date = new Date(), format = "DD-MM-YYYY" }) => {
  return moment(date).format(format);
};

const getOrdinalSuffix = dayOfMonth => {
  const lastDigit = dayOfMonth % 10,
    lastTwoDigits = dayOfMonth % 100;
  if (lastDigit == 1 && lastTwoDigits != 11) {
    return "st";
  }
  if (lastDigit == 2 && lastTwoDigits != 12) {
    return "nd";
  }
  if (lastDigit == 3 && lastTwoDigits != 13) {
    return "rd";
  }
  return "th";
};

export const formatDateWithOrdinal = ({ date = new Date() }) => {
  const momentDate = moment(date);
  const dayOfMonth = momentDate.format("D");
  const ordinal = getOrdinalSuffix(dayOfMonth);
  const formattedDate = momentDate.format(`DD[${ordinal}] MMM YYYY`);
  return formattedDate;
};

export const formatDateAsUtc = ({
  date = new Date(),
  format = "DD-MM-YYYY",
}) => {
  return moment(date).utc().format(format);
};

export const formatTimeString = ({ time, preFormat, postFormat }) => {
  return moment(time, preFormat).format(postFormat);
};

export const getValidTimeIn12HourFormat = ({ inputStr, minuteStep }) => {
  /**
    Util function to handle all the cases like:
    input not containing numbers and incomplete input
  */
  const minuteArr = [];
  for (let i = 0; i <= 60; i += minuteStep) {
    minuteArr.push(i < 10 ? `0${i}` : `${i}`);
  }
  const inputWithOnlyDigits = _.replace(inputStr, /[^\d]/g, "");
  const inputWithDigitsAndColons = _.replace(inputStr, /[^\d:]/g, "");
  const firstTwoDigits = inputWithOnlyDigits.substring(0, 2);
  let hourStr = "";
  let minuteStr = "";
  let isAM = !_.includes(_.toUpper(inputStr), "P");
  if (!inputWithDigitsAndColons) {
    // if input string has no digits or colon, it is invalid
    return null;
  }
  if (_.includes(inputStr, firstTwoDigits) && _.parseInt(firstTwoDigits) < 25) {
    // hour has 2 digits and it is in the range of 01 to 24
    hourStr = inputWithOnlyDigits.substring(0, 2);
    minuteStr = inputWithOnlyDigits.substring(2, 4);
  } else {
    // hour has just one digit or it is more than 24
    hourStr = "0" + inputWithOnlyDigits.substring(0, 1);
    minuteStr = inputWithOnlyDigits.substring(1, 3);
  }
  if (_.parseInt(minuteStr) >= 60) {
    // minute value should be between 00 and 59
    minuteStr = "00";
  }
  if (!hourStr || hourStr == "00") {
    hourStr = "12";
  }
  if (_.parseInt(hourStr) > 12) {
    // converting hourStr in the range of 01 to 12
    hourStr = (_.parseInt(hourStr) - 12).toString();
    isAM = false;
  }
  if (!minuteStr) {
    minuteStr = "00";
  }
  // appending 0 at the front of hour and minute if necessary
  hourStr = ("0" + hourStr).slice(-2);
  minuteStr = ("0" + minuteStr).slice(-2);
  return `${hourStr}:${minuteStr} ${isAM ? "am" : "pm"}`;
};

export const getValueInAM_PMFormat = value => {
  /**
    Converts a time string input to a
    12-hour time format string
  */
  if (_.isEmpty(value)) {
    return "09:00 am";
  }
  const isValueIn24HourFormat = !(value.length > 5);
  const correctValue = isValueIn24HourFormat
    ? moment(value, "H:mm").format("hh:mm a")
    : value;
  return correctValue;
};

export const getNearestTimeFromGivenArray = ({
  val,
  minuteArr,
  selectedTimeFormat = "hh:mm a",
}) => {
  /**
    Returns the current or the next greater value of time label
    for example:
    input: 10:05 am, minuteStep = 15, output: 10:15 am
    input: 10:15 am, minuteStep = 15, output: 10:15 am
  */
  const minuteVal = _.parseInt(val.substring(3, 5));
  const nextVal = _.first(_.filter(minuteArr, minute => minute >= minuteVal));
  let diff = 0;
  if (nextVal || nextVal === 0) {
    diff = nextVal - minuteVal;
  } else {
    diff = 60 - nextVal;
  }
  const newVal = moment(val, selectedTimeFormat)
    .add(diff, "minutes")
    .format(selectedTimeFormat);
  return newVal;
};

export const getDayNameOrIndex = (value, type = "index") => {
  const mappings = [
    [0, "sunday"],
    [1, "monday"],
    [2, "tuesday"],
    [3, "wednesday"],
    [4, "thursday"],
    [5, "friday"],
    [6, "saturday"],
  ];

  const match = _.find(mappings, ([day, name]) => {
    return type === "index" ? day === value : name === value;
  });
  return type === "index" ? match[1] : match[0];
};

export const getShorthandDayNameOrIndex = (value, type = "index") => {
  const mappings = [
    [0, "sun"],
    [1, "mon"],
    [2, "tue"],
    [3, "wed"],
    [4, "thu"],
    [5, "fri"],
    [6, "sat"],
  ];

  const match = _.find(mappings, ([day, name]) => {
    return type === "index" ? day === value : name === value;
  });
  return type === "index" ? match[1] : match[0];
};

export const getOperationalDayName = (operationalDays, t, isShortHand) => {
  return _.map(operationalDays, dayIndex => {
    const dayName = isShortHand
      ? getShorthandDayNameOrIndex(dayIndex)
      : getDayNameOrIndex(dayIndex);
    const formattedDayName = t(`common:${dayName}`);
    return formattedDayName;
  });
};

const DAYS_IN_ORDER_MAP = {
  shortName: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  fullName: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
};

export const getDaysNameOrIndexArray = ({
  array,
  type,
  format = "shortName",
}) => {
  const DAYS_IN_ORDER =
    DAYS_IN_ORDER_MAP[format] || DAYS_IN_ORDER_MAP["shortName"];

  if (type == "index") {
    return _.map(array, day => _.indexOf(DAYS_IN_ORDER, day));
  }
  return _.map(array, day => DAYS_IN_ORDER[day]);
};

export const getMinuteArr = ({ minuteStep }) => {
  const minuteArr = [];
  for (let i = 0; i <= 60; i += minuteStep) {
    minuteArr.push(i);
  }
  return minuteArr;
};

export const getTimelineLabels = ({
  interval,
  period,
  startTime = "00:00",
  minuteDifference,
  allowedDropdownOptionsStartTime,
  allowedDropdownOptionsEndTime,
}) => {
  const periodsInADay = moment.duration(1, "day").as(period);
  const timeLabels = [];
  const startTimeMoment = moment(
    allowedDropdownOptionsStartTime || "00:00",
    "HH:mm"
  );
  for (let i = 0; i <= periodsInADay; i += interval) {
    startTimeMoment.add(i === 0 ? 0 : interval, period);

    const isTimeOptionInvalid =
      allowedDropdownOptionsEndTime &&
      startTimeMoment.isAfter(moment(allowedDropdownOptionsEndTime, "HH:mm"));

    if (isTimeOptionInvalid) {
      break;
    }

    timeLabels.push({
      label: `${startTimeMoment.format("hh:mm a")}${
        minuteDifference ? ` (${i + minuteDifference} mins)` : ""
      }`,
      value: startTimeMoment.format("hh:mm a"),
    });
  }
  const startTimeInCorrectFormat = getValueInAM_PMFormat(startTime);
  const filteredTimeLabels = _.filter(timeLabels, timeLabel => {
    const difference = moment(startTimeInCorrectFormat, "hh:mm a").diff(
      moment(timeLabel.label, "hh:mm a")
    );
    // returning only those labels that have value greater than
    // or equal to the start time
    return difference <= 0;
  });
  return filteredTimeLabels;
};

export const isValueBeforeStartTime = ({ startTime, timeValue }) => {
  const startTimeIn12hourFormat = getValueInAM_PMFormat(startTime);
  const timeValueIn12HourFormat = getValueInAM_PMFormat(timeValue);
  return moment(timeValueIn12HourFormat, "hh:mm a").isBefore(
    moment(startTimeIn12hourFormat, "hh:mm a")
  );
};

export const getTimeRange = ({ start, end }) => {
  const result = [];
  for (let i = start; i <= end; i++) {
    result.push(i);
  }
  return result;
};

export const getExcludedRange = ({ start = 0, excludedOptions, end }) => {
  const arr = [];
  for (let value = start; value <= end; value++) {
    if (excludedOptions.indexOf(value) < 0) {
      arr.push(value);
    }
  }
  return arr;
};

export const getDisabledHours = ({ startTime, endTime, getFixHours }) => {
  if (getFixHours) {
    return getExcludedRange({
      start: 0,
      excludedOptions: getFixHours(),
      end: 23,
    });
  } else if (!!startTime && !!endTime) {
    const startHour = moment(startTime).get("hour");
    const endHour = moment(endTime).get("hour");
    const excludedHours = getTimeRange({ start: startHour, end: endHour });
    return getExcludedRange({
      start: 0,
      excludedOptions: excludedHours,
      end: 23,
    });
  } else {
    return [];
  }
};

export const getDisabledMinutes = ({
  hours,
  startTime,
  endTime,
  getFixMinutes,
}) => {
  if (getFixMinutes) {
    return getExcludedRange({
      start: 0,
      excludedOptions: getFixMinutes(hours),
      end: 59,
    });
  } else if (!!startTime && !!endTime) {
    let disabledMinutes = [];
    const startHour = moment(startTime).get("hour");
    const endHour = moment(endTime).get("hour");
    if (hours == startHour) {
      const startMinute = moment(startTime).get("minute");
      disabledMinutes = getTimeRange({ start: 0, end: startMinute - 1 });
    } else if (hours == endHour) {
      const endMinute = moment(endTime).get("minute");
      disabledMinutes = getTimeRange({ start: endMinute + 1, end: 59 });
    }
    return disabledMinutes;
  } else {
    return [];
  }
};

export const getOperationalDaysOptions = ({
  schedulerConstants,
  operationalDaysFromConstants,
}) => {
  const { week_start_day = 0, week_length = 7 } = schedulerConstants || {};
  const weekDaysArr = getWeekDaysArrWithEnLocal();
  if (!_.isUndefined(operationalDaysFromConstants)) {
    return operationalDaysFromConstants.map(day =>
      moment(weekDaysArr[day], "ddd", "en").locale("en").format("ddd")
    );
  }
  const options = [];
  for (let i = 0; i < week_length; i++) {
    options.push(
      moment(weekDaysArr[week_start_day], "ddd")
        .locale("en")
        .add(i, "day")
        .format("ddd")
    );
  }
  return options;
};

export const getOperationalDaysOptionsMemoize = _.memoize(
  params => getOperationalDaysOptions(params),
  params => JSON.stringify(params)
);

export const getOperationalDaysOptionsV2 = ({
  schedulerConstants,
  operationalDaysFromConstants,
}) => {
  const { week_start_day = 0, week_length = 7 } = schedulerConstants || {};
  const weekDaysArr = getWeekDaysArrWithEnLocal();

  if (!_.isUndefined(operationalDaysFromConstants)) {
    return _.map(operationalDaysFromConstants, day => _.get(weekDaysArr, day));
  }

  return _.slice(weekDaysArr, week_start_day, week_length + 1);
};

export const getOperationalDaysOptionsV2Memoize = _.memoize(
  params => getOperationalDaysOptionsV2(params),
  params => JSON.stringify(params)
);

export const getOperationalDays = ({
  schedulerConstants,
  operationalDaysFromConstants,
}) => {
  const options = getOperationalDaysOptionsMemoize({
    schedulerConstants,
    operationalDaysFromConstants,
  });
  return _.map(options, item => _.indexOf(DAYS_IN_ORDER, item));
};

export const getOperationalDaysMemoize = _.memoize(
  params => getOperationalDays(params),
  params => JSON.stringify(params)
);

export const createOperationalDaysValueSet = ({ operationalDaysValue }) => {
  const weekDaysArr = getWeekDaysArr();
  const operationalDays = _.map(operationalDaysValue, day =>
    _.indexOf(weekDaysArr, day)
  ).sort();
  return operationalDays;
};

export const getOperationalDates = ({
  weekStartDate,
  weekEndDate,
  schedulerConstants,
  operationalDaysFromConstants,
}) => {
  const operationalDays = getOperationalDaysOptions({
    schedulerConstants,
    operationalDaysFromConstants,
  });
  const dateArray = [];
  const currentDate = moment(weekStartDate);
  let dayCount = 1;
  while (
    dayCount <= 7 &&
    (weekEndDate
      ? moment(currentDate).isSameOrBefore(weekEndDate, "day")
      : true)
  ) {
    if (_.includes(operationalDays, currentDate.locale("en").format("ddd"))) {
      dateArray.push(currentDate.format("YYYY-MM-DD"));
    }
    dayCount++;
    currentDate.add(1, "days");
  }
  return {
    operationalDates: dateArray,
    startDate: _.first(dateArray),
    endDate: _.last(dateArray),
  };
};

const formatDateString = ({ start, end, options = {} }) => {
  // customize formating of different scenarios if required
  const {
    diffYearFormatFn,
    diffMonthFormatFn,
    diffDayFormatFn,
    sameDayFormatFn,
    isDeadlineAllDay = true,
  } = options;
  // both the dates are same
  if (moment(start).isSame(end, "day")) {
    if (sameDayFormatFn) {
      return sameDayFormatFn(start);
    }
    return isDeadlineAllDay
      ? moment(start).format(DATE_TIME_CONSTANTS.ddd_MMM_DD)
      : moment(start).utc().format(DATE_TIME_CONSTANTS.ddd_MMM_DD_h_mm_A);
  } else if (moment(start).format("YYYY") !== moment(end).format("YYYY")) {
    // years are different
    if (diffYearFormatFn) {
      return diffYearFormatFn(start, end);
    }
    return `${moment(start).format(DATE_TIME_CONSTANTS.ddd_MMM_DD)} - ${moment(
      end
    ).format(DATE_TIME_CONSTANTS.ddd_MMM_DD)}`;
  } else if (moment(start).format("MMM") !== moment(end).format("MMM")) {
    // months are different
    if (diffMonthFormatFn) {
      return diffMonthFormatFn(start, end);
    }
    return `${moment(start).format(DATE_TIME_CONSTANTS.ddd_MMM_DD)} - ${moment(
      end
    ).format(DATE_TIME_CONSTANTS.ddd_MMM_DD)}`;
  } else {
    // days are different
    if (diffDayFormatFn) {
      return diffDayFormatFn(start, end);
    }
    return `${moment(start).format(DATE_TIME_CONSTANTS.ddd_MMM_DD)} - ${moment(
      end
    ).format(DATE_TIME_CONSTANTS.ddd_MMM_DD)}`;
  }
};
export const formatDateStringMemoize = _.memoize(
  params => formatDateString(params),
  params => JSON.stringify(params)
);

const groupRotationDays = ({ rotationDays, noOfDaysinRotationCycle }) => {
  let currIndex = -1;
  return _.reduce(
    rotationDays,
    (result, obj, index) => {
      // first item of the group
      if (!(index % noOfDaysinRotationCycle)) {
        result.push({
          start: obj.date,
          items: [],
        });
        currIndex++;
      } else if (
        // last item of the group
        index % noOfDaysinRotationCycle ===
        noOfDaysinRotationCycle - 1
      ) {
        result[currIndex].end = obj.date;
      }
      result[currIndex].items.push(obj);
      return result;
    },
    []
  );
};

export const groupRotationDaysMemoize = _.memoize(
  params => groupRotationDays(params),
  params => JSON.stringify(params)
);

const _checkIsMomentIsoString = ({ dateString }) => {
  return moment(dateString, moment.ISO_8601, true).isValid();
};

export const checkIsMomentIsoString = _.memoize(
  params => _checkIsMomentIsoString(params),
  params => JSON.stringify(params)
);

const _getDateTimeStringsBasedOnFormat = ({
  inputDateTimeString,
  dateTimeFormat = "Do, MMM YYYY hh:mm a",
}) => {
  try {
    if (inputDateTimeString) {
      const dateLabelString = moment(inputDateTimeString, "YYYY-MM-DD").format(
        "Do, MMM YYYY"
      );
      const dateLabelStringWithoutComma = moment(
        inputDateTimeString,
        "YYYY-MM-DD"
      ).format("Do MMM YYYY");
      const isDateTimeIsoFormat = checkIsMomentIsoString({
        dateString: inputDateTimeString,
      });
      if (isDateTimeIsoFormat) {
        const isValidTimeString = moment(
          inputDateTimeString,
          moment.ISO_8601,
          true
        ).isValid();
        return {
          dateString: moment(inputDateTimeString).format("YYYY-MM-DD"),
          timeString: isValidTimeString
            ? moment(inputDateTimeString).format("HH:mm")
            : null,
          dateLabelString,
          dateLabelStringWithoutComma,
          dateTimeString: isValidTimeString
            ? moment(inputDateTimeString, moment.ISO_8601).format(
                dateTimeFormat
              )
            : moment(inputDateTimeString, "YYYY-MM-DD").format("Do, MMM YYYY"),
        };
      } else {
        const isValidTimeString = moment(
          inputDateTimeString,
          "YYYY-MM-DD:HH:mm",
          true
        ).isValid();
        return {
          dateString: moment(inputDateTimeString, "YYYY-MM-DD").format(
            "YYYY-MM-DD"
          ),
          dateLabelString,
          dateLabelStringWithoutComma,
          timeString: isValidTimeString
            ? moment(inputDateTimeString, "YYYY-MM-DD:HH:mm").format("HH:mm")
            : null,
          dateTimeString: isValidTimeString
            ? moment(inputDateTimeString, "YYYY-MM-DD:HH:mm").format(
                dateTimeFormat
              )
            : moment(inputDateTimeString, "YYYY-MM-DD").format("Do, MMM YYYY"),
        };
      }
    } else {
      return {
        dateString: null,
        timeString: null,
        dateTimeString: "",
        dateLabelString: "",
      };
    }
  } catch {
    return {
      dateString: null,
      timeString: null,
      dateTimeString: "",
      dateLabelString: "",
    };
  }
};

export const getDateTimeStringsBasedOnFormat = getMemoizedItemsFunction({
  fn: _getDateTimeStringsBasedOnFormat,
  items: ["TIMEZONE"],
});

const _getTimeDifferenceFromToday = ({ dateTimeString, diffrenceKey }) => {
  const fromDateTimeString = moment(dateTimeString, "DD-MM-YYYY HH:mm");
  const today = moment();
  return today.diff(fromDateTimeString, diffrenceKey);
};

export const getTimeDifferenceFromToday = _.memoize(
  params => _getTimeDifferenceFromToday(params),
  params => JSON.stringify(params)
);

const getTimeTableTimeRange = ({ startTime, endTime }) =>
  startTime && endTime
    ? `${moment(startTime, "HH:mm:ss").format("h:mm")} -
  ${moment(endTime, "HH:mm:ss").format("h:mm a")}`
    : "--:--:-- - --:--:--";

export const getTimeTableTimeRangeMemoize = _.memoize(
  getTimeTableTimeRange,
  params => JSON.stringify(params)
);

export const getDatesDifferenceInDays = ({ startDate, endDate }) => {
  const momentStartDate = moment(startDate);
  const momentEndDate = moment(endDate);

  const differentInMs = moment(momentEndDate).diff(momentStartDate);
  const msInADay = 86400 * 1000;

  return differentInMs / msInADay;
};

export const checkIsDateSame = ({
  date1,
  date2,
  check = ATTENDANCE_CONSTANTS.DAY,
}) => {
  return moment(date1).isSame(date2, check);
};

export const getFormattedStartDate = ({ initialDate, format, startOfType }) => {
  if (startOfType) {
    return moment(initialDate).startOf(startOfType).format(format);
  }
  return moment(initialDate).format(format);
};

export const getFormattedEndDate = ({ initialDate, format, endOfType }) => {
  if (endOfType) {
    return moment(initialDate).endOf(endOfType).format(format);
  }
  return moment(initialDate).format(format);
};

export const getTodaysDate = () => {
  return moment();
};

export const getPreviousDayDate = date => {
  return moment(date).subtract(1, "day");
};

export const getNextDayDate = date => {
  return moment(date).add(1, "day");
};

export const getWeekOfYear = () => {
  return moment().isoWeek();
};

export const getStartOfDay = () => {
  return moment().startOf("day");
};

export const getAddedTimeMoment = ({
  key,
  value,
  time,
  format,
  preFormat,
  postFormat,
}) => {
  const preTimeFormat = preFormat || format;
  const postTimeFormat = postFormat || format;
  return moment(time, preTimeFormat).add(value, key).format(postTimeFormat);
};

export const isStartDateAfterEndDate = ({ startDate, endDate }) => {
  return moment(startDate).isAfter(moment(endDate), DATE_TIME_CONSTANTS.DAY);
};

export const getDateArrBetweenDates = ({ start, end }) => {
  const result = [];
  const current = moment(start);
  while (current.isSameOrBefore(moment(end), DATE_TIME_CONSTANTS.DAY)) {
    result.push({
      eventDate: moment(current),
    });
    current.add(1, DATE_TIME_CONSTANTS.DAY);
  }
  return result;
};

export const checkIsDateBefore = ({
  date1,
  date2,
  key = DATE_TIME_CONSTANTS.DAY,
}) => {
  return moment(date1).isBefore(moment(date2), key);
};

export const checkIsDateAfter = ({
  date1,
  date2,
  key = DATE_TIME_CONSTANTS.DAY,
}) => {
  return moment(date1).isAfter(moment(date2), key);
};

export const checkIsSameMonth = ({ date1, date2 }) => {
  return moment(date1).isSame(moment(date2), "month");
};

export const isDateInBetween = ({
  date,
  date1,
  date2,
  unitType = "day",
  dateIncludes = "[]",
}) => {
  return moment(date).isBetween(date1, date2, unitType, dateIncludes);
};

export const isDateSameOrAfter = (date1, date2) => {
  return moment(date1).isSameOrAfter(moment(date2));
};

export const getIncrementedDate = ({ date, key, incrementBy = 0 }) => {
  return moment(date).add(incrementBy, key);
};

export const getDecrementedDate = ({ date, key, decrementBy = 0 }) => {
  return moment(date).subtract(decrementBy, key);
};

const generateCalendarMonthOptions = ({ startDate, endDate }) => {
  const options = [];
  let date = endDate;

  while (moment(date).isSameOrAfter(startDate, ATTENDANCE_CONSTANTS.MONTH)) {
    const currDate = moment(date);

    options.push({
      label: currDate.format(DATE_TIME_CONSTANTS.MMMM_YYYY),
      value: currDate,
    });

    date = formatDate({
      date: getDecrementedDate({
        date,
        decrementBy: 1,
        key: ATTENDANCE_CONSTANTS.MONTH,
      }),
      format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
    });
  }

  return options;
};

export const generateCalendarMonthOptionsMemoized = _.memoize(
  params => generateCalendarMonthOptions(params),
  params => JSON.stringify(params)
);

export const getIsoWeek = ({ date }) => {
  return moment(date).isoWeek();
};

export const isFormattedDateSame = ({ date1, date2, format }) => {
  const formattedDate1 = formatDate({ date: date1, format });
  const formattedDate2 = formatDate({ date: date2, format });
  return moment(formattedDate1).isSame(formattedDate2);
};

export const getISODate = ({ date, startOfKey = DATE_TIME_CONSTANTS.DAY }) => {
  return moment(date).startOf(startOfKey).toDate().toISOString();
};

export const getStartOfDate = ({ date, key = DATE_TIME_CONSTANTS.DAY }) => {
  return moment(date).startOf(key);
};

export const getEndOfDate = ({ date, key = DATE_TIME_CONSTANTS.DAY }) => {
  return moment(date).endOf(key);
};

export const isDateSameOrBefore = (date1, date2) => {
  return moment(date1).isSameOrBefore(moment(date2));
};

export const getDayNumber = date => {
  const day = moment(date).locale("en").format(DATE_TIME_CONSTANTS.ddd);
  return _.indexOf(DAYS_IN_ORDER, day);
};

export const getDateTimeDifference = ({
  date1,
  date2,
  differenceBy = DATE_TIME_CONSTANTS.DAY,
}) => {
  return moment(date1).diff(date2, differenceBy);
};

export const getStartDateOfWeek = (date, week) => {
  return moment(date).isoWeek(week).day(0);
};

export const getTimeDifferenceFromCurrentTime = ({ t, date }) => {
  const today = moment();
  const todayFormatted = today.format("YYYY-MM-DD");
  const createdAtFormatted = moment(date).format("YYYY-MM-DD");
  const requestedAgo = moment(todayFormatted).diff(createdAtFormatted, "days");
  let count;
  if ((count = Math.floor(requestedAgo / 30))) {
    return t("common:sent_count_ago_month", {
      count: count,
    });
  } else if ((count = Math.floor(requestedAgo / 7))) {
    return t("common:sent_count_ago_week", {
      count: count,
    });
  } else {
    const lastModifiedToday = moment(todayFormatted).isSame(
      createdAtFormatted,
      "days"
    );
    if (lastModifiedToday) {
      if ((count = Math.floor(moment(today).diff(date, "hours")))) {
        return t("common:sent_count_ago_hour", {
          count: count,
        });
      } else if ((count = Math.floor(moment(today).diff(date, "minutes")))) {
        return t("common:sent_count_ago_minute", {
          count: count,
        });
      } else {
        return t("common:sent_just_now");
      }
    } else {
      return t("common:sent_count_ago_day", {
        count: requestedAgo,
      });
    }
  }
};

export const getTimeFromMins = ({ mins }) => {
  const h = (mins / 60) | 0;
  const m = mins % 60 | 0;
  const formattedHours = moment().hours(h).minutes(m).format("hh:mm");
  return mins > 0 ? `+${formattedHours}` : `-${formattedHours}`;
};

// Check if the difference between start and end time is less than or Equal To given duration
export const isDifferenceLessThanDuration = ({
  startTime,
  endTime,
  duration,
  timeUnit,
}) => {
  const difference = moment(endTime).diff(moment(startTime), timeUnit);
  return difference < duration;
};

// Function to get the ordinal text for the week of the month
export const getWeekOrdinalText = ({ weekOfMonth, t }) => {
  switch (weekOfMonth) {
    case 1:
      return t("common:lowercase", { text: t("common:first") });
    case 2:
      return t("common:lowercase", { text: t("common:second") });
    case 3:
      return t("common:lowercase", { text: t("common:third") });
    case 4:
      return t("common:lowercase", { text: t("common:fourth") });
    default:
      return t("common:lowercase", { text: t("common:last") });
  }
};

export const getWeekDaysNameArray = () => {
  return moment.weekdays();
};

export const WEEK_DAYS = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
};

export const getFormattedDuration = ({
  duration,
  unit = "seconds",
  durationFormat = "mm:ss",
  trim = false,
}) => {
  return moment.duration(duration, unit).format(durationFormat, { trim });
};

export const getDateInUtc = (date = new Date()) => {
  return moment(date).utc();
};

export const checkIsDateSameOrAfter = ({
  date1,
  date2,
  key = DATE_TIME_CONSTANTS.DAY,
}) => {
  return moment(date1).isSameOrAfter(moment(date2), key);
};

export const isFutureDateTime = dateTime => {
  const futureDateTime = moment(dateTime);
  const currDateTime = moment();

  return futureDateTime.isAfter(currDateTime);
};

export const getFormattedTime = (dateTime, timeFormat = "hh:mm") => {
  if (!dateTime) return null;
  return moment(dateTime).format(timeFormat);
};

export const getNextThirtyMinuteSlot = () => {
  // Get the current time
  const now = moment();

  // Round up to the next 30-minute slot
  const minutes = now.minutes();
  if (minutes < 30) {
    now.minutes(30).seconds(0);
  } else {
    now.add(1, "hour").minutes(0).seconds(0);
  }

  // Create the start and end times
  const startDateTime = moment(now);
  const endDateTime = moment(now).add(30, "minutes");

  const startDate = formatDate({
    date: startDateTime,
    format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
  });
  const endDate = formatDate({
    date: endDateTime,
    format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
  });
  const startTime = formatDate({
    date: startDateTime,
    format: DATE_TIME_CONSTANTS.HH_mm,
  });
  const endTime = formatDate({
    date: endDateTime,
    format: DATE_TIME_CONSTANTS.HH_mm,
  });

  return { startDate, startTime, endDate, endTime };
};

export const isValidDateTimeString = (dateString, format) => {
  // Ensure dateString is valid string otherwise the function will return true in case of undefined
  if (!_.isString(dateString) || _.isEmpty(dateString)) {
    return false;
  }

  return moment(dateString, format).isValid();
};

export const getISOString = date => {
  return moment(date).toISOString();
};

// Function to check if a date is in this week

// Function to check if a date is in next week

// function to check is startdate time is after end date time
export const isStartDateTimeAfterEndDateTime = ({
  startDateTime,
  endDateTime,
  format = DATE_TIME_CONSTANTS.DD_MMM_YYYY_HH_mm,
}) => {
  return moment(startDateTime, format).isAfter(moment(endDateTime, format));
};

export const calculateTimeDifferenceInMinutes = ({ startTime, endTime }) => {
  const duration = moment.duration(endTime.diff(startTime));
  return parseInt(duration.asMinutes());
};
